/* Copyright IBM Corp. 2018 */
import { Observable } from 'rxjs/Observable';
import { isArray } from 'rxjs/util/isArray';
import { isFunction } from 'rxjs/util/isFunction';

import { JsonMap } from '../json/json.utils';

/*
 * helper
 */
const _toString = Object.prototype.toString;

/*
 * Invokes the native toString method
 *
 * @param aValue value to call for
 * @return the result of the method
 */
function _applyToString(aValue: any): string {
  return _toString.apply(aValue);
}

/*
 * Result for a string object
 */
const _TO_STRING_STRING = _applyToString('');

/*
 * Result for a function object
 */
const _TO_STRING_FUNCTION = _applyToString(_applyToString);

/*
 * Tests if an object is a string
 *
 * @param aValue    value to test
 */
function _isString(aValue: any): aValue is string {
  return _applyToString(aValue) === _TO_STRING_STRING;
}

/**
 * Tests if a value is a number value
 *
 * @param aValue    the value
 * @return true if the value is number, else false
 */
function _isNumber(aValue: any): aValue is number {
  return typeof aValue === 'number';
}

/**
 * Tests if a value is a date
 *
 * @param aValue    the value
 * @return true if the value is date, else false
 */
function _isDate(aValue: any): aValue is Date {
  return aValue instanceof Date;
}

/**
 * Tests if a value is a boolean value
 *
 * @param aValue    the value
 * @return true if the value is boolean, else false
 */
function _isBoolean(aValue: any): aValue is boolean {
  return typeof aValue === 'boolean';
}

/*
 * Checks for undefined
 *
 * @param aObject   object to check
 * @return the return value
 */
function _isUndefined(aObject: any): aObject is undefined {
  return aObject === undefined;
}


/*
 * Checks for nil
 *
 * @param aObject   object to check
 * @return the return value
 */
function _isNil(aObject: any): aObject is null | undefined {
  return _isUndefined(aObject) || (aObject === null);
}

/*
* Checks for nil
*
* @param aObject   object to check
* @return the return value
*/
function _isNotNil(aObject: any): boolean {
  return !_isNil(aObject);
}

const _getPrototypeOf = Object.getPrototypeOf;
const EMPTY_PROTOTYPE = _getPrototypeOf({});

/*
 * Tests for a plain object
 *
 * @param aObject   the object to test
 */
function _isPlainObject(aObject: any): aObject is JsonMap {
  return aObject && !aObject.prototype && (typeof aObject === 'object') && (_getPrototypeOf(aObject) === EMPTY_PROTOTYPE);
}

/*
 * Tests if the value if a valid observable
 *
 * @param aValue    the value
 * @return true if the value is a valid obserable, else false
 */
function _isObservable(aValue: any): aValue is Observable<any> {
  return aValue instanceof Observable;
}

/**
 * Tests if a URL is an absolute URL
 *
 * @param aUrl  the URL string
 * @return true if the URL is absolute, else false
 */
function _isAbsoluteUrl(aUrl: string): boolean {
  return aUrl && (aUrl.startsWith('http://') || aUrl.startsWith('https://'));
}

export type IsPredicate<T> = (aValue: any) => aValue is T;

/**
 * Tests if all elements of the array are of a particular type
 *
 * @param aValue the value to test
 * @param aPredicate the predicate to test each element with
 *
 * @return true if the value is an array and all elements are of the specific type
 */
function _isArrayOf<T>(aValue: any, aPredicate: IsPredicate<T>): aValue is T[] {
  // test each array member
  return isArray(aValue) && aValue.every(aPredicate);
}

/**
 * Tests if all elements of the array are of type string
 *
 * @param aValue the value to test
 * @param aPredicate the predicate to test each element with
 *
 * @return true if the value is an array and all elements are of the specific type
 */
function _isStringArray(aValue: any): aValue is string[] {
  return _isArrayOf(aValue, _isString);
}

/**
 * Tests if all fields of an object are of a particular type
 *
 * @param aValue the value to test
 * @param aPredicate the predicate to test each element with
 *
 * @return true if the value is an array and all elements are of the specific type
 */
function _isObjectOf<T>(aValue: any, aPredicate: IsPredicate<T>): aValue is { [key: string]: T } {
  // test if the value is an object
  if (!_isPlainObject(aValue)) {
    return false;
  }
  // test each object
  // tslint:disable-next-line:forin
  for (const key in aValue) {
    // test
    if (!aPredicate(aValue[key])) {
      return false;
    }
  }
  // ok
  return true;
}

/**
 * Tests if all fields of an object are of a particular type
 *
 * @param aValue the value to test
 * @param aPredicate the predicate to test each element with
 *
 * @return true if the value is an array and all elements are of the specific type
 */
function _isParseableAsDate(aValue: any): aValue is string | number | Date {
  // check if the value can be parsed as a string
  return _isNotNil(aValue) && (_isString(aValue) || _isNumber(aValue) || _isDate(aValue));
}

export type Predicate<T> = (aValue: T) => boolean;

export type EqualsPredicate<T> = (aLeft: T, aRight: T) => boolean;

export {
  _isNil as isNil,
  _isNotNil as isNotNil,
  _isUndefined as isUndefined,
  _isString as isString,
  _isNumber as isNumber,
  _isBoolean as isBoolean,
  _isObservable as isObservable,
  _isAbsoluteUrl as isAbsoluteURL,
  isArray,
  isFunction,
  _isDate as isDate,
  _isParseableAsDate as isParseableAsDate,
  _isPlainObject as isPlainObject,
  _isStringArray as isStringArray,
  _isArrayOf as isArrayOf,
  _isObjectOf as isObjectOf
};
